import { useMemo, useState } from 'react';
import { useCustomFields, useMetaForm, useUpdateMetaForm } from '../api/hooks';
import type { MetaCoreColumn, MetaFieldMapping, MetaMappingTarget } from '../api/types';
import { ErrorBanner, Modal } from '../components/ui';

/**
 * The field-mapping editor.
 *
 * This is the screen that matters on the whole integration. Everything else
 * arranges plumbing; a wrong choice here writes a wrong value onto every future
 * lead from this form, silently, for as long as the campaign runs. So: nothing
 * is saved implicitly, suggestions are visibly marked as suggestions until a
 * human presses Save, and unmapped questions are shown rather than hidden.
 */

const CORE_COLUMNS: Array<{ value: MetaCoreColumn; label: string }> = [
  { value: 'full_name', label: 'Full name' },
  { value: 'first_name', label: 'First name' },
  { value: 'last_name', label: 'Last name' },
  { value: 'email', label: 'Email' },
  { value: 'phone_e164', label: 'Phone' },
  { value: 'alt_phone_e164', label: 'Alternate phone' },
];

/** The <select> speaks strings; these two translate to and from the API shape. */
function encodeTarget(target: MetaMappingTarget | undefined): string {
  if (!target) return '';
  if (target.target === 'core') return `core:${target.column}`;
  if (target.target === 'custom') return `custom:${target.api_key}`;
  return 'ignore';
}

function decodeTarget(value: string): MetaMappingTarget | null {
  if (!value) return null;
  if (value === 'ignore') return { target: 'ignore' };
  const [kind, rest] = value.split(':');
  if (kind === 'core') return { target: 'core', column: rest as MetaCoreColumn };
  if (kind === 'custom') return { target: 'custom', api_key: rest ?? '' };
  return null;
}

export function MetaFormMappingModal({ formId, onClose }: { formId: string; onClose: () => void }) {
  const { data: form, isPending } = useMetaForm(formId);
  const { data: customFields = [] } = useCustomFields();
  const updateForm = useUpdateMetaForm();

  const [draft, setDraft] = useState<MetaFieldMapping | null>(null);
  const [error, setError] = useState<unknown>(null);

  /**
   * The starting point is the saved mapping *plus* the server's suggestions for
   * questions that have none. The suggestions are pre-selected but marked, so
   * saving is an explicit confirmation rather than something that happened
   * while the admin was reading.
   */
  const initial = useMemo<MetaFieldMapping>(() => {
    if (!form) return {};
    return { ...(form.suggested_mapping ?? {}), ...form.field_mapping };
  }, [form]);

  const mapping = draft ?? initial;
  const suggestedKeys = useMemo(() => {
    if (!form) return new Set<string>();
    const saved = new Set(Object.keys(form.field_mapping ?? {}));
    return new Set(Object.keys(form.suggested_mapping ?? {}).filter((k) => !saved.has(k)));
  }, [form]);

  const dirty = draft !== null || suggestedKeys.size > 0;

  function setQuestion(key: string, value: string) {
    const target = decodeTarget(value);
    const next = { ...mapping };
    if (target) next[key] = target;
    else delete next[key];
    setDraft(next);
  }

  async function save() {
    setError(null);
    try {
      await updateForm.mutateAsync({ id: formId, field_mapping: mapping });
      onClose();
    } catch (err) {
      // The server validates every target against the live custom-field list
      // and returns a message naming each problem — show it as it came.
      setError(err);
    }
  }

  const activeFields = customFields.filter((f) => f.is_active);
  const mappedCount = Object.values(mapping).filter((t) => t.target !== 'ignore').length;
  const questions = form?.questions ?? [];

  return (
    <Modal
      title={form ? `Field mapping · ${form.name}` : 'Field mapping'}
      onClose={onClose}
      width={760}
      footer={
        <>
          <span className="hint" style={{ marginRight: 'auto' }}>
            {mappedCount} of {questions.length} questions mapped
          </span>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={updateForm.isPending || !dirty || isPending}
          >
            {updateForm.isPending ? 'Saving…' : 'Save mapping'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />

      {isPending && <div className="hint">Loading form…</div>}

      {form && questions.length === 0 && (
        <div className="banner banner-info">
          This form has no questions stored yet. Sync the page's forms again to pull them from Meta.
        </div>
      )}

      {form && questions.length > 0 && (
        <>
          {suggestedKeys.size > 0 && (
            <div className="banner banner-info" style={{ marginBottom: 12 }}>
              {suggestedKeys.size} question{suggestedKeys.size === 1 ? ' has a' : 's have'} suggested
              mapping{suggestedKeys.size === 1 ? '' : 's'}, matched by name. Nothing is applied until
              you save.
            </div>
          )}

          <div className="meta-map-list">
            {questions.map((question, index) => {
              const key = question.key ?? '';
              if (!key) return null;
              const isSuggested = suggestedKeys.has(key) && !draft;
              const current = encodeTarget(mapping[key]);

              return (
                <div className="meta-map-row" key={`${key}-${index}`}>
                  <div className="meta-map-question">
                    <b>{question.label || key}</b>
                    <span className="mono hint">{key}</span>
                    {question.type && <span className="tag">{question.type.toLowerCase()}</span>}
                  </div>

                  <div className="meta-map-target">
                    <select
                      value={current}
                      onChange={(e) => setQuestion(key, e.target.value)}
                      aria-label={`Mapping for ${question.label || key}`}
                      data-suggested={isSuggested ? 'true' : undefined}
                    >
                      <option value="">Not mapped — kept in attribution</option>
                      <option value="ignore">Ignore this answer</option>
                      <optgroup label="Lead fields">
                        {CORE_COLUMNS.map((c) => (
                          <option key={c.value} value={`core:${c.value}`}>
                            {c.label}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Custom fields">
                        {activeFields.map((f) => (
                          <option key={f.api_key} value={`custom:${f.api_key}`}>
                            {f.label}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                    {isSuggested && <span className="chip chip-plain">suggested</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="hint" style={{ marginTop: 14 }}>
            An answer that is not mapped is never lost — it is stored on the lead's attribution
            record. Mapping it only decides whether it also lands on a field you can filter and
            report on.
          </p>
        </>
      )}
    </Modal>
  );
}
